import {
    createFileRoute,
    Link,
    useNavigate,
    useSearch,
} from "@tanstack/react-router";
import { useEffect } from "react";
import {
    FormProvider,
    useForm,
    DeepPartial,
    Controller,
} from "react-hook-form";
import { object, string, boolean, InferType, number } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    addToast,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    useDisclosure,
    Card,
    CardBody,
    CardHeader,
} from "@heroui/react";
import TextInput from "@/components/inputs/TextInput";
import SelectInput from "@/components/inputs/SelectInput";
import CheckboxInput from "@/components/inputs/CheckboxInput";
import useRequest from "@/lib/request/useRequest";
import sentinelHelmet from "@/assets/images/sentinel-helmet.png";
import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png";

type PublicProvider = {
    id: number;
    name: string;
    url_slug: string;
    logo_url?: string | null;
};

const RegisterSchema = object({
    first_name: string().required(""),
    last_name: string().required(""),
    email: string().email().required(""),
    organization_name: string().required(""),
    account_type: string().oneOf(["grower", "consultant"]).required(""),
    provider_id: number().when("account_type", {
        is: (t: string) => t === "grower",
        then: (s) => s.required(""),
        otherwise: (s) => s.optional(),
    }),
    agreed_to_terms: boolean().oneOf(
        [true],
        "Please accept the Terms to continue",
    ),
    username: string().default(""),
    phone_number: string().default(""),
    address_1: string().default(""),
    address_2: string().default(""),
    city: string().default(""),
    state: string().default(""),
    zip: string().default(""),
    countryCode: string().default("+1"),
    phone_display: string().default(""),
});
type RegisterForm = InferType<typeof RegisterSchema>;

function initials(n: string) {
    const p = n.trim().split(/\s+/).filter(Boolean);
    const f = p[0]?.[0] ?? "";
    const l = p.length > 1 ? p[p.length - 1]?.[0] : "";
    return (f + l).toUpperCase();
}
function BrandLockup({
    name,
    logoUrl,
}: {
    name: string;
    logoUrl?: string | null;
}) {
    return (
        <div className="flex flex-col items-center text-center gap-3 md:gap-4">
            {logoUrl ? (
                <img
                    src={logoUrl}
                    alt={`${name} logo`}
                    className="h-16 w-auto max-w-[14rem] md:h-20 md:max-w-[20rem] object-contain"
                    loading="eager"
                />
            ) : (
                <div
                    aria-hidden
                    className="h-14 w-14 md:h-16 md:w-16 grid place-items-center rounded bg-black/20  text-lg md:text-xl font-semibold"
                >
                    {initials(name)}
                </div>
            )}
            <span className="text-2xl md:text-4xl font-semibold leading-tight break-words">
                {name}
            </span>
            <span aria-hidden className="h-px w-16 md:w-24 bg-primary-500" />
            <span className=" text-lg md:text-3xl font-medium">
                Sentinel Ag
            </span>
        </div>
    );
}

export const Route = createFileRoute("/(auth)/register/$cspSlug")({
    component: RegisterSlugRoute,
});

function RegisterSlugRoute() {
    const { cspSlug } = Route.useParams();
    return <RegisterPage cspSlug={cspSlug} />;
}

export default function RegisterPage({ cspSlug }: { cspSlug?: string }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();

    const { signup_source: signupSource } = useSearch({
        from: "/(auth)/register",
    });

    const { useGetRequest, useMutateRequest } = useRequest();

    const {
        data: providersResp,
        isLoading: providersLoading,
        error: providersErr,
    } = useGetRequest<PublicProvider[]>({ url: "organizations/public/" });
    const providers = providersResp ?? [];

    const provider = cspSlug
        ? (providers.find(
              (p) => (p.url_slug ?? "").toLowerCase() === cspSlug.toLowerCase(),
          ) ?? null)
        : null;

    useEffect(() => {
        if (!cspSlug) return;
        if (providersLoading) return;
        if (providersErr || !provider) {
            navigate({
                to: "/register",
                replace: true,
                search: (prev) => prev,
            });
        }
    }, [cspSlug, providersLoading, providersErr, provider, navigate]);

    useEffect(() => {
        if (provider?.name) document.title = `${provider.name} | Sentinel Ag`;
    }, [provider?.name]);

    const defaultValues: DeepPartial<RegisterForm> = {
        countryCode: "+1",
        agreed_to_terms: false,
        ...(cspSlug
            ? { account_type: "grower", provider_id: provider?.id ?? 0 }
            : {}),
    };
    const f = useForm<RegisterForm>({
        resolver: yupResolver(RegisterSchema),
        mode: "onBlur",
        defaultValues,
    });

    useEffect(() => {
        if (!cspSlug || !provider) return;
        f.setValue("account_type", "grower", { shouldValidate: true });
        f.setValue("provider_id", provider.id, { shouldValidate: true });
    }, [cspSlug, provider, f]);

    const accountType = f.watch("account_type");
    useEffect(() => {
        if (cspSlug) return;
        if (accountType !== "grower") {
            f.setValue("provider_id", 0, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [cspSlug, accountType, f]);

    const createUser = useMutateRequest({ url: "users/", method: "post" });

    const onSubmit = async (values: RegisterForm) => {
        const emailLower = values.email.toLowerCase();
        const username = emailLower;
        const digits = (values.phone_display || "")
            .replace(/\D/g, "")
            .slice(-10);
        const cc = values.countryCode?.startsWith("+")
            ? values.countryCode
            : `+${values.countryCode || "1"}`;
        const phone_number = `${cc}${digits}`;

        const payload = {
            first_name: values.first_name,
            last_name: values.last_name,
            email: emailLower,
            username,
            phone_number,
            address_1: values.address_1,
            address_2: values.address_2,
            city: values.city,
            state: values.state,
            zip: values.zip,
            organization_name: values.organization_name,
            account_type: cspSlug ? "grower" : values.account_type,
            provider_id: cspSlug ? (provider?.id ?? 0) : values.provider_id,
            agreed_to_terms: values.agreed_to_terms,
            ...(cspSlug && provider
                ? {
                      service_provider_id: provider.id,
                      service_provider_slug: provider.url_slug,
                  }
                : {}),
            signup_source: signupSource,
        };

        try {
            await createUser.mutateAsync(
                { data: payload },
                {
                    onSuccess: () => {
                        onOpen();
                    },
                },
            );
        } catch {
            addToast({
                description:
                    "Registration failed. Please check your details and try again.",
                color: "danger",
            });
        }
    };

    const blurb = (
        <div className="p-4 flex flex-col gap-4 pt-12 w-full">
            <div className=" font-bold text-lg">Sentinel® Insights</div>
            <div className="text-3xl font-semibold">
                Profitable and confident Nitrogen management
            </div>
            <div>
                Sentinel optimizes your nitrogen applications to achieve the
                most profitable outcome on your farm. It does this by measuring
                crop N demand and suggesting when, where, and how much N
                fertilizer to apply. Sentinel has been used to achieve NCGA Low
                N contest championship wins and has delivered $24/ac improvement
                in profitability in benchmarking trials.
            </div>
        </div>
    );

    return (
        <div className="flex flex-col items-center md:grid md:grid-cols-[1fr_2fr] min-h-screen w-full md:h-screen md:justify-items-center md:p-0">
            <div className=" w-full h-full hidden md:block overflow-hidden ">
                <div className="flex flex-col h-full w-full text-primary-500 ">
                    {cspSlug && provider ? (
                        <div className="h-full w-full grid place-items-center ">
                            <BrandLockup
                                name={provider.name}
                                logoUrl={provider.logo_url ?? undefined}
                            />
                        </div>
                    ) : (
                        <>
                            {blurb}
                            <div className="w-full h-full flex justify-center items-center">
                                <img
                                    src={sentinelHelmet}
                                    className="w-[20rem] h-auto object-contain"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="pt-8 px-2 w-full md:overflow-y-auto md:h-screen flex flex-col items-center justify-center">
                <Card className="w-full max-w-[30rem] mx-auto">
                    <CardHeader></CardHeader>
                    <CardBody>
                        <FormProvider {...f}>
                            <form
                                onSubmit={f.handleSubmit(onSubmit)}
                                noValidate
                            >
                                <div className="flex flex-col w-[90%] mx-auto items-stretch md:max-w-[36rem] gap-4">
                                    <img
                                        src={sentinelLogo}
                                        alt="Sentinel"
                                        className="block w-full max-w-[18rem] md:max-w-[25rem] mx-auto px-6 md:px-8 h-auto-4 mb-4"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <TextInput
                                            name="first_name"
                                            label="First Name"
                                            size="sm"
                                        />
                                        <TextInput
                                            name="last_name"
                                            label="Last Name"
                                            size="sm"
                                        />
                                    </div>

                                    <TextInput
                                        name="email"
                                        label="Email"
                                        type="email"
                                        size="sm"
                                    />
                                    <TextInput
                                        name="organization_name"
                                        label="Organization Name"
                                        size="sm"
                                    />

                                    {cspSlug ? (
                                        <>
                                            <input
                                                type="hidden"
                                                readOnly
                                                value="grower"
                                                {...f.register("account_type")}
                                            />
                                            <input
                                                type="hidden"
                                                readOnly
                                                value={provider?.name ?? ""}
                                                {...f.register("provider_id")}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <SelectInput
                                                name="account_type"
                                                label="Account Type"
                                                size="sm"
                                                placeholder="Select..."
                                                options={[
                                                    {
                                                        id: "grower",
                                                        description: "Grower",
                                                    },
                                                    {
                                                        id: "consultant",
                                                        description:
                                                            "Consultant",
                                                    },
                                                ]}
                                            />
                                            {accountType === "grower" && (
                                                <SelectInput
                                                    name="provider_id"
                                                    label="Provider"
                                                    size="sm"
                                                    placeholder={
                                                        providersLoading
                                                            ? "Loading providers..."
                                                            : providers.length
                                                              ? "Select..."
                                                              : "No providers available"
                                                    }
                                                    options={providers.map(
                                                        (p) => ({
                                                            id: p.id,
                                                            description: p.name,
                                                        }),
                                                    )}
                                                    isDisabled={
                                                        providersLoading ||
                                                        !providers.length
                                                    }
                                                />
                                            )}
                                            {!providersLoading &&
                                                !!providersErr && (
                                                    <p className="text-xs text-red-600 -mt-2">
                                                        Couldn’t load providers.
                                                        Try again later.
                                                    </p>
                                                )}
                                        </>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-4">
                                        <TextInput
                                            name="countryCode"
                                            label="Country Code"
                                            type="tel"
                                            size="sm"
                                            inputMode="tel"
                                            maxLength={5}
                                            placeholder="+1"
                                        />
                                        <TextInput
                                            name="phone_display"
                                            label="Phone"
                                            type="tel"
                                            size="sm"
                                            inputMode="tel"
                                            maxLength={14}
                                            onChange={() => {
                                                const v =
                                                    f.getValues(
                                                        "phone_display",
                                                    ) ?? "";
                                                const d = v
                                                    .replace(/\D/g, "")
                                                    .slice(-10);
                                                const p1 = d.slice(0, 3),
                                                    p2 = d.slice(3, 6),
                                                    p3 = d.slice(6);
                                                const formatted = d
                                                    ? p3
                                                        ? `(${p1}) ${p2}-${p3}`
                                                        : p2
                                                          ? `(${p1}) ${p2}`
                                                          : `(${p1}`
                                                    : "";
                                                f.setValue(
                                                    "phone_display",
                                                    formatted,
                                                    {
                                                        shouldDirty: true,
                                                        shouldValidate: true,
                                                    },
                                                );
                                            }}
                                        />
                                    </div>

                                    <TextInput
                                        name="address_1"
                                        label="Address line 1"
                                        size="sm"
                                    />
                                    <TextInput
                                        name="address_2"
                                        label="Address line 2"
                                        size="sm"
                                    />
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <TextInput
                                            name="city"
                                            label="City"
                                            size="sm"
                                        />
                                        <TextInput
                                            name="state"
                                            label="State"
                                            size="sm"
                                        />
                                        <TextInput
                                            name="zip"
                                            label="ZIP"
                                            size="sm"
                                        />
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <Controller
                                            name="agreed_to_terms"
                                            control={f.control}
                                            render={({ field }) => (
                                                <CheckboxInput
                                                    name={field.name}
                                                    id="agreed_to_terms"
                                                    isSelected={!!field.value}
                                                    onValueChange={
                                                        field.onChange
                                                    }
                                                    onBlur={field.onBlur}
                                                    className="h-4 w-4"
                                                    size="sm"
                                                />
                                            )}
                                        />
                                        <label
                                            htmlFor="agreed_to_terms"
                                            className="text-sm ml-2"
                                        >
                                            I agree to the{" "}
                                            <a
                                                href="/documents/termsOfService.pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline cursor-pointer text-gray-500 hover:text-[#008ea9]"
                                            >
                                                Terms of Service,
                                            </a>{" "}
                                            <a
                                                href="/documents/dataUseAgreement.pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline cursor-pointer text-gray-500 hover:text-[#008ea9]"
                                            >
                                                Data Use Agreement,
                                            </a>{" "}
                                            <a
                                                href="/documents/privacyPolicy.pdf"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline cursor-pointer text-gray-500 hover:text-[#008ea9]"
                                            >
                                                Privacy Policy,
                                            </a>{" "}
                                            and to receive text messages from
                                            Sentinel, message and data rates may
                                            apply, reply STOP to opt out
                                        </label>
                                    </div>

                                    {f.formState.errors.agreed_to_terms && (
                                        <p className="mt-[-0.25rem] text-xs text-red-600">
                                            {
                                                f.formState.errors
                                                    .agreed_to_terms
                                                    .message as string
                                            }
                                        </p>
                                    )}

                                    <Button
                                        type="submit"
                                        color="primary"
                                        size="sm"
                                        variant="solid"
                                        isLoading={f.formState.isSubmitting}
                                        isDisabled={f.formState.isSubmitting}
                                    >
                                        Create Account
                                    </Button>

                                    <div className="text-sm mb-2">
                                        Already have an account?{" "}
                                        <Link
                                            to="/auth"
                                            className="text-brandGreen-500 underline"
                                        >
                                            Log in
                                        </Link>
                                    </div>
                                </div>
                            </form>
                        </FormProvider>
                    </CardBody>
                </Card>
            </div>

            {!cspSlug && (
                <div className="md:hidden mt-12 text-primary-500 flex-1 flex flex-col">
                    {blurb}
                    <div className="w-full flex justify-center pb-12">
                        <img
                            src={sentinelHelmet}
                            className="w-[60%] h-auto object-contain "
                        />
                    </div>
                </div>
            )}

            <Modal
                isOpen={isOpen}
                onOpenChange={(open) => (open ? onOpen() : onClose())}
                isDismissable={false}
            >
                <ModalContent>
                    <ModalBody className="py-6">
                        <p className="mb-4 text-base font-medium">
                            Thanks for registering!
                        </p>
                        <Button
                            onPress={() => {
                                onClose();
                                navigate({ to: "/auth", replace: true });
                            }}
                        >
                            Go to login
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
}
